// Disjuntor da GRAVAÇÃO do catálogo — por capability OBSERVADA, nunca por
// modelo de aparelho.
//
// ── O defeito que isto fecha ──
// Com o `iptv-catalog` vazio, cada revalidação baixa o catálogo inteiro (sem
// cópia local não há ETag pra mandar, então o servidor sempre responde 200) e
// tenta gravá-lo de novo. Não havia freio: o `write.quotaExceeded` ia pra
// telemetria e ninguém o usava pra parar. Numa TV cujo teto oscila entre zero e
// alguns MB, isso é uma tentativa por boot + duas por hora, 5 a 15 MB cada.
//
// E a medição no aparelho mostrou que tentar CUSTA mesmo quando falha: escrever
// e apagar não devolve o espaço na hora, e um único teste derrubou a capacidade
// de 1 MB pra 100 KB. Cada tentativa do catálogo come a capacidade que os
// favoritos e o progresso precisam. O disjuntor não é otimização — é o que para
// a sangria.
//
// ── Os três modos ──
//   full           padrão: grava as 6 surfaces.
//   priority-only  o aparelho já mostrou pressão de cota: grava SÓ o que é do
//                  caminho do vídeo (`liveChannels` + as 3 categorias, que somam
//                  KB) e nunca mais tenta `series`/`vodItems` — que degradam pra
//                  rede sem quebrar nada. É o registro antigo: o canal ao vivo não
//                  perde o cache por causa dos 15 mil títulos de VOD.
//   off            nem a prioridade coube: não grava NADA. O catálogo vem da rede,
//                  exatamente como já vinha — o cache nunca funcionou aqui, então
//                  desligar a tentativa não tira nada do usuário.
//
// ── Só DESCE sozinho. Sobe só por ação do usuário. ──
// Rearme automático está vetado por decisão TÉCNICA do coordenador (não do
// Fernando, não de review), apoiada num INDÍCIO do aparelho: na TV do dono, as
// escritas de teste de uma medição foram seguidas de a maior escrita aceita cair
// de 1 MB para 100 KB, segundos depois. O próprio agente que mediu chamou de
// "indício, não confirmação": as duas medições "antes" discordaram 10x sem nada
// mudar entre elas. Não é "uma tentativa leva a capacidade a zero" (este
// comentário dizia isso; era mais do que a medição mostra). O rearme é:
//   • "Atualizar catálogo" (manual): uma tentativa LIMITADA ao tamanho da
//     prioridade (~400 KB), e o aparelho só sai de `off` se ELA chegar ao
//     `set()` e a prioridade entrar inteira (ver `registrarResultadoManual`).
//     Nunca volta a `full` por esse botão.
//   • "Limpar dados" (hard reset): o `localStorage.clear()` de lá apaga este
//     estado e o aparelho recomeça em `full`. É a ação pesada e deliberada. O
//     primeiro boot depois dele tenta gravar o catálogo inteiro, UMA vez.
//
// ⚠️ NÃO "conserte" o hard reset pra preservar o disjuntor. Parece mais seguro e
// é pior: nenhum aparelho voltaria a `full` nunca mais, e quem liberou espaço
// ficaria preso em `priority-only` pra sempre. Uma tentativa depois de um reset
// EXPLÍCITO do usuário é custo aceito: quem limpa dados está recomeçando e pode
// ter liberado espaço. (Decisão registrada no changelog da entrega.)
//
// Alternativas descartadas, registradas de propósito:
//   • Rearme diário automático: é exatamente a tentativa que custa capacidade,
//     agendada — e o ganho (cachear o que talvez não caiba) não paga o risco.
//   • Rearme no boot: vetado pela mesma razão, e seria a máquina de volta.
//   • Tentativa periódica com teto de tamanho: o veto a DEIXAVA aberta ("ou com
//     teto de tamanho"); descartá-la foi extensão do implementador, pelo
//     mesmo indício acima. Em reavaliação (item 4 do review do `181b1f81`): a
//     proposta é uma tentativa automática por versão nova do app. Não
//     implementada.
//   • Manual voltando a `full`: um toque dispararia 5-15 MB de uma vez, com um
//     custo que o usuário não tem como enxergar.
//
// ── Persistência: melhor esforço, com piso na SESSÃO ──
// O modo vai pro `localStorage` quando ele aceita escrita. Quando não aceita
// (o mesmo disco cheio pode recusá-lo), o modo continua valendo na memória do
// módulo pela sessão inteira — o disjuntor nunca DESARMA por falta de onde
// persistir, só deixa de lembrar no próximo boot. Nesse pior caso a sangria cai
// de "1 por boot + 2 por hora" para "1 por boot".
//
// COMPAT Chr38: só JSON, try/catch e Date.now.

import {
  SURFACE_SACRIFICIAL,
  SURFACE_WRITE_PRIORITY,
  type SnapshotWriteResult,
  type SurfaceName,
} from './catalog-store'

export type CatalogWriteMode = 'full' | 'priority-only' | 'off'

const STORAGE_KEY = 'swipe:catalog-write-mode:v1'

/** Surfaces do caminho do vídeo, DERIVADAS da ordem de prioridade menos as
 *  sacrificáveis — uma fonte só, sem segunda lista pra divergir. */
export const PRIORITY_SURFACES: readonly SurfaceName[] = SURFACE_WRITE_PRIORITY.filter(
  (s) => !SURFACE_SACRIFICIAL.includes(s),
)

/** Estado da sessão. Vence o persistido: é o que garante o piso quando o
 *  `localStorage` recusa escrita. */
let sessionMode: CatalogWriteMode | null = null
/** Quando o modo da sessão começou (ms). Anda junto com `sessionMode`. */
let sessionDesde: number | null = null

const RANK: Record<CatalogWriteMode, number> = { full: 2, 'priority-only': 1, off: 0 }

function isMode(v: unknown): v is CatalogWriteMode {
  return v === 'full' || v === 'priority-only' || v === 'off'
}

function readPersisted(): CatalogWriteMode | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    const mode = parsed && typeof parsed === 'object' ? (parsed as { mode?: unknown }).mode : null
    return isMode(mode) ? mode : null
  } catch {
    return null
  }
}

/** `since` do modo persistido (ms), se houver um número válido. */
function readPersistedDesde(): number | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    const since = parsed && typeof parsed === 'object' ? (parsed as { since?: unknown }).since : null
    return typeof since === 'number' && isFinite(since) ? since : null
  } catch {
    return null
  }
}

/** @returns true se persistiu. Falhar aqui NÃO desarma nada (ver cabeçalho). */
function persist(mode: CatalogWriteMode, since: number): boolean {
  try {
    if (typeof localStorage === 'undefined') return false
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, since }))
    return true
  } catch {
    return false
  }
}

function setMode(mode: CatalogWriteMode): void {
  const since = Date.now()
  sessionMode = mode
  sessionDesde = since
  persist(mode, since)
}

/** Modo vigente: o da sessão, senão o persistido, senão `full`. */
export function catalogWriteMode(): CatalogWriteMode {
  return sessionMode ?? readPersisted() ?? 'full'
}

/**
 * Desde quando (ms) o aparelho está no modo vigente — o início do EPISÓDIO.
 * A auto-cura (catalog-auto-cura.ts) o usa pra fazer UM reset por episódio de
 * `off`. `null` = não se sabe (modo padrão nunca gravado, ou registro de uma
 * versão sem o campo).
 */
export function modoDesde(): number | null {
  if (sessionMode !== null) return sessionDesde
  return readPersisted() === null ? null : readPersistedDesde()
}

/** O que gravar neste modo. `null` = não grava nada. */
export function surfacesFor(mode: CatalogWriteMode): readonly SurfaceName[] | null {
  if (mode === 'off') return null
  if (mode === 'priority-only') return PRIORITY_SURFACES
  return SURFACE_WRITE_PRIORITY
}

/**
 * Aprende com o resultado de uma gravação. SÓ DESCE, nunca sobe.
 *
 * Só COTA desarma. `unavailable` (IDB inteiro ausente, o piso sem Proxy) não é
 * pressão: é ambiente, e já degrada pra rede sozinho.
 *
 * @returns o modo depois de aprender.
 */
export function noteWriteResult(r: SnapshotWriteResult): CatalogWriteMode {
  const atual = catalogWriteMode()
  if (!r.quotaExceeded) return atual
  // A prioridade inteira entrou? Então dá pra manter o caminho do vídeo em
  // cache; senão, nem ela cabe e o certo é parar de tentar.
  const prioridadeEntrou = PRIORITY_SURFACES.every((s) => r.persisted.includes(s))
  const alvo: CatalogWriteMode = prioridadeEntrou ? 'priority-only' : 'off'
  if (RANK[alvo] < RANK[atual]) setMode(alvo)
  return catalogWriteMode()
}

/**
 * Modo em que roda UMA tentativa MANUAL ("Atualizar catálogo"): de `off` sobe
 * até `priority-only` — nunca a `full` —, SÓ PRA ESTA TENTATIVA. Não persiste
 * nada e não muda o modo do aparelho.
 *
 * ⚠️ Antes o rearme era persistido ANTES da tentativa (review do `181b1f81`,
 * P2): se a tentativa manual não chegava ao `set()` (rede caída, prazo), o
 * próximo poll AUTOMÁTICO encontrava o aparelho já em `priority-only` e gravava
 * — consumindo pelo automático o rearme que só o usuário podia dar, contra o
 * próprio veto. Agora o rearme viaja COM a tentativa (`loadSnapshot(..., {
 * manual: true })`) e só é consumido em `registrarResultadoManual`, quando ela
 * de fato chegou ao `set()`.
 */
export function modoDaTentativaManual(): CatalogWriteMode {
  const atual = catalogWriteMode()
  return atual === 'off' ? 'priority-only' : atual
}

/**
 * A tentativa MANUAL chegou ao `set()` e voltou: é aqui que o rearme é
 * consumido.
 *  • A prioridade inteira entrou, sem cota: o aparelho SAI de `off` (vai a
 *    `priority-only` e persiste). Nunca sobe além disso.
 *  • Qualquer outro desfecho: aprende como o automático (`noteWriteResult`,
 *    que só desce). Quem estava em `off` continua em `off`.
 * Prazo estourado NÃO chega aqui (não há resultado pra aprender).
 */
export function registrarResultadoManual(r: SnapshotWriteResult): CatalogWriteMode {
  const prioridadeEntrou = PRIORITY_SURFACES.every((s) => r.persisted.includes(s))
  if (catalogWriteMode() === 'off' && prioridadeEntrou && !r.quotaExceeded) {
    setMode('priority-only')
    return catalogWriteMode()
  }
  return noteWriteResult(r)
}

// ── Memória da última TENTATIVA de gravação (review do `c8268b84`, P2) ──
// O teto de frequência da prioridade (catalog-snapshot,
// PRIORITY_REWRITE_MIN_INTERVAL_MS) media só desde o `writtenAt` do META — e o
// meta só existe se ENTROU. Uma gravação em `priority-only` cujo meta não entra
// (erro que não é cota; prazo estourado depois do `delete meta` do `set()`) não
// deixava rastro nenhum, e a regravação repetia a cada poll, sem teto (sonda do
// review: 10 polls, 10 `set()`).
//
// Por isso a tentativa é registrada AQUI, ANTES do `set()` — o prazo estourado
// nunca devolve nada, então registrar depois perderia justamente esse caso. Vai
// pro `localStorage`, que grava na TV do dono (é outro pool; medido no
// aparelho), com o mesmo piso de sessão do modo. As duas memórias se somam: o teto segura se QUALQUER uma disser
// "recente" (`writtenAt` no IndexedDB, tentativa no localStorage).
//
// Chave própria, ao lado da do modo e com a mesma política: sem um
// read-modify-write num objeto só, que poderia apagar o modo ao gravar a
// tentativa (ou o contrário). Por provedor: só a última, com o id junto — a
// tentativa de um provedor não segura o teto de outro.
const TENTATIVA_KEY = 'swipe:catalog-write-attempt:v1'

interface Tentativa {
  providerId: string
  /** `Date.now()` do instante em que a gravação ia começar (ms). */
  em: number
}

let sessionTentativa: Tentativa | null = null

function lerTentativa(): Tentativa | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(TENTATIVA_KEY)
    if (!raw) return null
    const v: unknown = JSON.parse(raw)
    if (v === null || typeof v !== 'object') return null
    const { providerId, em } = v as { providerId?: unknown; em?: unknown }
    return typeof providerId === 'string' && typeof em === 'number' && isFinite(em) ? { providerId, em } : null
  } catch {
    return null
  }
}

/** Uma gravação do catálogo VAI começar agora. Chamar ANTES do `set()`. */
export function registrarTentativa(providerId: string, em: number): void {
  sessionTentativa = { providerId, em }
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(TENTATIVA_KEY, JSON.stringify(sessionTentativa))
  } catch {
    // Piso: vale na sessão (ver o cabeçalho, "Persistência").
  }
}

/** Instante (ms) da última tentativa DESTE provedor, ou `null`. */
export function ultimaTentativa(providerId: string): number | null {
  const t = sessionTentativa ?? lerTentativa()
  return t !== null && t.providerId === providerId ? t.em : null
}

/** Só pra teste: zera a memória da sessão (o persistido o teste controla). */
export function __resetCatalogWriteBreakerForTest(): void {
  sessionMode = null
  sessionDesde = null
  sessionTentativa = null
}
